import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AppHeader } from "../components/AppHeader";
import { apiFetch } from "../lib/api";
import { uploadIssueAttachment } from "../lib/uploadIssueAttachment";
import { useMe } from "../hooks/useMe";
import { resolveMembership } from "../hooks/useFamilyMembership";
import { can, ISSUE_CATEGORIES } from "@family/shared";
import type { IssueCategoryKey } from "@family/shared";

interface IssueAttachment {
  id: string;
  url: string;
}

interface Issue {
  id: string;
  title: string;
  description: string | null;
  category: string;
  status: string;
  createdBy: string;
  reporterName: string | null;
  createdAt: string;
  updatedAt: string;
  commentCount: number;
  attachments: IssueAttachment[];
}

interface IssueComment {
  id: string;
  body: string;
  authorId: string;
  authorName: string | null;
  createdAt: string;
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  open: { label: "Open", color: "var(--c-danger)" },
  in_progress: { label: "In Progress", color: "var(--c-accent)" },
  resolved: { label: "Resolved", color: "var(--c-success)" },
  closed: { label: "Closed", color: "var(--c-text-subtle)" },
};

function categoryLabel(key: string): string {
  return ISSUE_CATEGORIES.find((c) => c.key === key)?.label ?? key;
}

export function IssuesPage() {
  const meQuery = useMe();
  const primary = resolveMembership(meQuery.data);
  const slug = primary?.familySlug;
  const myUserId = meQuery.data?.user.id;
  const isAdmin = primary ? can(primary.role, "members:manage") : false;
  const queryClient = useQueryClient();

  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newCategory, setNewCategory] = useState<IssueCategoryKey>("other");
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [expandedIssue, setExpandedIssue] = useState<string | null>(null);
  const [showClosed, setShowClosed] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const issuesQuery = useQuery<{ issues: Issue[] }>({
    queryKey: ["issues", slug],
    queryFn: () => apiFetch(`/families/${slug}/issues`),
    enabled: Boolean(slug),
  });

  const updateIssue = useMutation({
    mutationFn: ({ issueId, status }: { issueId: string; status: string }) =>
      apiFetch(`/families/${slug}/issues/${issueId}`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["issues", slug] }),
  });

  const deleteIssue = useMutation({
    mutationFn: (issueId: string) =>
      apiFetch(`/families/${slug}/issues/${issueId}`, { method: "DELETE" }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["issues", slug] }),
  });

  function resetForm() {
    setShowCreate(false);
    setNewTitle("");
    setNewDesc("");
    setNewCategory("other");
    setNewFiles([]);
    setSubmitError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  async function submitIssue(e: React.FormEvent) {
    e.preventDefault();
    if (!newTitle.trim() || !slug || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const created = await apiFetch<{ id: string }>(`/families/${slug}/issues`, {
        method: "POST",
        body: JSON.stringify({
          title: newTitle.trim(),
          description: newDesc.trim() || undefined,
          category: newCategory,
        }),
      });
      // Upload screenshots one by one after the issue exists. A failed
      // screenshot doesn't lose the report — surface it and keep the rest.
      const failures: string[] = [];
      for (const file of newFiles) {
        try {
          await uploadIssueAttachment(slug, created.id, file);
        } catch (err) {
          failures.push(`${file.name}: ${err instanceof Error ? err.message : "upload failed"}`);
        }
      }
      void queryClient.invalidateQueries({ queryKey: ["issues", slug] });
      if (failures.length > 0) {
        setSubmitError(`Your feedback was submitted, but some screenshots failed — ${failures.join("; ")}`);
        setNewFiles([]);
        if (fileInputRef.current) fileInputRef.current.value = "";
      } else {
        resetForm();
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Could not submit your feedback.");
    } finally {
      setSubmitting(false);
    }
  }

  if (!primary || !slug) {
    return <><AppHeader /><main className="page"><p className="text-muted">Loading…</p></main></>;
  }

  const allIssues = issuesQuery.data?.issues ?? [];
  const hiddenCount = allIssues.filter((i) => i.status === "resolved" || i.status === "closed").length;
  const visibleIssues = showClosed
    ? allIssues
    : allIssues.filter((i) => i.status !== "resolved" && i.status !== "closed");

  return (
    <>
      <AppHeader />
      <main className="page stack-lg">
        <h1>Feedback</h1>
        {/* Inline margin-top replaces the .stack-lg gap (it doesn't subtract
            from it), so this is the full heading-to-subtitle distance. */}
        <p className="text-muted" style={{ marginTop: "var(--s-2)" }}>
          Spotted a problem with the site, or have an idea to make it better?
          Share it here — everyone can see what's been reported and its
          status, so check the list before filing a duplicate.
        </p>

        <section className="stack">
          {!showCreate && (
            <button onClick={() => setShowCreate(true)} style={{ alignSelf: "flex-start" }}>
              + Give feedback
            </button>
          )}

          {showCreate && (
            <form onSubmit={submitIssue} className="card stack">
              <h3 style={{ margin: 0, fontSize: "1rem" }}>New feedback</h3>
              <div>
                <label htmlFor="iss-title">Title *</label>
                <input id="iss-title" type="text" value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="Short summary of the problem" required />
              </div>
              <div>
                <label htmlFor="iss-category">Category</label>
                <select id="iss-category" value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value as IssueCategoryKey)}
                  style={{ width: "100%", padding: "10px 12px", fontSize: "1rem", border: "1px solid var(--c-border-strong)", borderRadius: 4, background: "var(--c-bg)", color: "var(--c-text)" }}>
                  {ISSUE_CATEGORIES.map((c) => (
                    <option key={c.key} value={c.key}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="iss-desc">Details</label>
                <textarea id="iss-desc" value={newDesc} onChange={(e) => setNewDesc(e.target.value)} rows={4}
                  placeholder="For a problem: what did you do, what did you expect, what happened instead, and on which device/browser? For an idea: what would you like to see?"
                  style={{ width: "100%", background: "var(--c-bg)", color: "var(--c-text)", border: "1px solid var(--c-border-strong)", borderRadius: 4, padding: "14px 16px", fontSize: "1rem", fontFamily: "inherit", resize: "vertical" }} />
              </div>
              <div>
                <label htmlFor="iss-files">Screenshots (optional)</label>
                <input id="iss-files" ref={fileInputRef} type="file" accept="image/*" multiple
                  onChange={(e) => setNewFiles(Array.from(e.target.files ?? []))} />
                {newFiles.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                    {newFiles.map((f, i) => (
                      <img key={`${f.name}-${i}`} src={URL.createObjectURL(f)} alt={f.name}
                        style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 6, border: "1px solid var(--c-border)" }} />
                    ))}
                  </div>
                )}
              </div>
              {submitError && (
                <p style={{ color: "var(--c-danger)", fontSize: "0.85rem", margin: 0 }}>{submitError}</p>
              )}
              <div style={{ display: "flex", gap: "var(--s-3)" }}>
                <button type="submit" disabled={submitting || !newTitle.trim()}>
                  {submitting ? "Submitting…" : "Submit"}
                </button>
                <button type="button" className="btn-secondary" onClick={resetForm}>Cancel</button>
              </div>
            </form>
          )}

          {issuesQuery.isLoading && <p className="text-muted">Loading…</p>}

          {!issuesQuery.isLoading && visibleIssues.length === 0 && (
            <p className="text-muted">
              {allIssues.length === 0 ? "No feedback yet." : "Nothing open. 🎉"}
            </p>
          )}

          {visibleIssues.map((issue) => {
            const st = STATUS_LABELS[issue.status] ?? STATUS_LABELS.open!;
            const canManage = isAdmin || issue.createdBy === myUserId;
            return (
              <div key={issue.id} className="card">
                <div className="row-between test-case-row">
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <strong>{issue.title}</strong>
                      <span style={{ fontSize: "0.75rem", fontWeight: 700, color: st.color, textTransform: "uppercase" }}>
                        {st.label}
                      </span>
                      <span style={{
                        fontSize: "0.7rem", padding: "2px 8px", borderRadius: "var(--r-pill)",
                        background: "var(--c-bg-alt)", border: "1px solid var(--c-border)", color: "var(--c-text-muted)",
                      }}>
                        {categoryLabel(issue.category)}
                      </span>
                    </div>
                    {issue.description && (
                      <p className="text-muted" style={{ fontSize: "0.85rem", marginTop: 4, whiteSpace: "pre-wrap" }}>{issue.description}</p>
                    )}
                    <div className="text-subtle" style={{ fontSize: "0.8rem", marginTop: 4 }}>
                      Reported by {issue.reporterName ?? "Unknown"} on{" "}
                      {new Date(issue.createdAt).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" })}
                    </div>
                    {issue.attachments.length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                        {issue.attachments.map((a) => (
                          <a key={a.id} href={a.url} target="_blank" rel="noreferrer">
                            <img src={a.url} alt="Issue screenshot"
                              style={{ width: 88, height: 88, objectFit: "cover", borderRadius: 6, border: "1px solid var(--c-border)" }} />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="test-case-controls" style={{ display: "flex", gap: "var(--s-2)", alignItems: "center" }}>
                    {canManage ? (
                      <select
                        value={issue.status}
                        onChange={(e) => updateIssue.mutate({ issueId: issue.id, status: e.target.value })}
                        style={{ padding: "4px 8px", fontSize: "0.8rem", borderRadius: 4, border: "1px solid var(--c-border-strong)", background: "var(--c-bg)" }}
                      >
                        <option value="open">Open</option>
                        <option value="in_progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </select>
                    ) : null}
                    <button type="button" className="btn-tertiary"
                      onClick={() => setExpandedIssue(expandedIssue === issue.id ? null : issue.id)}
                      style={{
                        fontSize: "0.8rem",
                        padding: "4px 10px",
                        minHeight: 28,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                      aria-label={
                        issue.commentCount
                          ? `${issue.commentCount} comment${issue.commentCount === 1 ? "" : "s"}`
                          : "Add comment"
                      }
                    >
                      💬{issue.commentCount ? <span>{issue.commentCount}</span> : null}
                    </button>
                    {canManage && (
                      <button type="button" className="btn-tertiary"
                        onClick={() => { if (window.confirm("Delete this feedback?")) deleteIssue.mutate(issue.id); }}
                        style={{ fontSize: "0.8rem", padding: "4px 8px", minHeight: 28, color: "var(--c-danger)" }}>
                        ✕
                      </button>
                    )}
                  </div>
                </div>
                {expandedIssue === issue.id && <IssueComments familySlug={slug} issueId={issue.id} />}
              </div>
            );
          })}

          {hiddenCount > 0 && (
            <button type="button" className="btn-tertiary" onClick={() => setShowClosed(!showClosed)}
              style={{ alignSelf: "flex-start", fontSize: "0.85rem" }}>
              {showClosed
                ? "Hide resolved & closed"
                : `Show resolved & closed (${hiddenCount})`}
            </button>
          )}
        </section>
      </main>
    </>
  );
}

function IssueComments({ familySlug, issueId }: { familySlug: string; issueId: string }) {
  const queryClient = useQueryClient();
  const [body, setBody] = useState("");

  const query = useQuery<{ comments: IssueComment[] }>({
    queryKey: ["issue-comments", issueId],
    queryFn: () => apiFetch(`/families/${familySlug}/issues/${issueId}/comments`),
  });

  const add = useMutation({
    mutationFn: (text: string) =>
      apiFetch(`/families/${familySlug}/issues/${issueId}/comments`, {
        method: "POST", body: JSON.stringify({ body: text }),
      }),
    onSuccess: () => {
      setBody("");
      void queryClient.invalidateQueries({ queryKey: ["issue-comments", issueId] });
      void queryClient.invalidateQueries({ queryKey: ["issues", familySlug] });
    },
  });

  const comments = query.data?.comments ?? [];

  return (
    <div style={{ marginTop: "var(--s-3)", paddingTop: "var(--s-3)", borderTop: "1px solid var(--c-border)" }}>
      {comments.map((c) => (
        <div key={c.id} style={{ marginBottom: 8 }}>
          <span style={{ fontWeight: 700, fontSize: "0.85rem" }}>{c.authorName ?? "Unknown"}</span>
          <span className="text-subtle" style={{ fontSize: "0.75rem", marginLeft: 8 }}>
            {new Date(c.createdAt).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" })}
          </span>
          <p style={{ margin: "2px 0 0", fontSize: "0.9rem" }}>{c.body}</p>
        </div>
      ))}
      <form onSubmit={(e) => { e.preventDefault(); if (body.trim()) add.mutate(body.trim()); }}
        style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <input type="text" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Add a comment…"
          style={{ flex: 1, padding: "6px 10px", fontSize: "0.85rem", border: "1px solid var(--c-border-strong)", borderRadius: 6, background: "var(--c-bg)", color: "var(--c-text)" }} />
        <button type="submit" disabled={add.isPending || !body.trim()}
          style={{ padding: "6px 14px", fontSize: "0.85rem", borderRadius: 6, minHeight: 32 }}>
          Post
        </button>
      </form>
    </div>
  );
}
